import type {IntlHook} from '@sail/intl';
import type {FieldPath, FieldValues, FieldRules} from './types';
import {
  requiredMsg,
  minLengthMsg,
  maxLengthMsg,
  minMsg,
  maxMsg,
  invalidFormatMsg,
} from './messages';

/**
 * Transforms validation rules to include default error messages when
 * boolean or numeric values are provided instead of custom messages.
 *
 * Examples:
 * - {required: true} → {required: 'This field is required'}
 * - {minLength: 5} → {minLength: {value: 5, message: 'Must be at least 5 characters'}}
 * - {required: 'Custom message'} → unchanged (custom message preserved)
 */
export function applyDefaultValidationMessages<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  formatMessage: IntlHook['formatMessage'],
  rules?: FieldRules<TFieldValues, TFieldName>,
): FieldRules<TFieldValues, TFieldName> | undefined {
  if (!rules) {
    return undefined;
  }

  const transformed = {...rules};

  if (transformed.required === true) {
    transformed.required = formatMessage(requiredMsg);
  }

  if (typeof transformed.minLength === 'number') {
    const value = transformed.minLength;
    transformed.minLength = {
      value,
      message: formatMessage(minLengthMsg, {value}),
    };
  }

  if (typeof transformed.maxLength === 'number') {
    const value = transformed.maxLength;
    transformed.maxLength = {
      value,
      message: formatMessage(maxLengthMsg, {value}),
    };
  }

  if (typeof transformed.min === 'number') {
    const value = transformed.min;
    transformed.min = {
      value,
      message: formatMessage(minMsg, {value}),
    };
  }

  if (typeof transformed.max === 'number') {
    const value = transformed.max;
    transformed.max = {
      value,
      message: formatMessage(maxMsg, {value}),
    };
  }

  if (transformed.pattern instanceof RegExp) {
    const pattern = transformed.pattern;
    transformed.pattern = {
      value: pattern,
      message: formatMessage(invalidFormatMsg),
    };
  }

  return transformed;
}
